import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  BENZ_F26048_QC_RULES,
  can,
  type ClauseTemplate,
  type QcRule,
  type TenantPaymentDetails,
  type TenantProfile,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, NumberField, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader, SectionHeader } from '../components/States.tsx';
import {
  addClauseVersion,
  loadTenantDocuments,
  savePayment,
  saveProfile,
  saveQcDefaults,
  type TenantDocuments,
} from './api.ts';
import { QcRulesEditor } from './QuoteEditor.tsx';
import { TableWrap } from '../components/TableWrap.tsx';

// Starting clause templates bundled per tenant (content/tenants/<slug>/clauses/*.json; Thomson's from TJR's text).
const BUNDLED = import.meta.glob('../../../../content/tenants/*/clauses/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, ClauseTemplate>;

const PART_TITLE: Readonly<Record<DocumentPart, string>> = {
  details: 'Details and payment',
  clauses: 'Documents and clauses',
  qc: 'QC defaults',
};

const PART_WORDS: Readonly<Record<DocumentPart, string>> = {
  details: 'What quotes and contracts print about the company, and where invoices are paid.',
  clauses: 'The clause templates quotes and contracts are printed with.',
  qc: 'The QC limits a new quote starts with.',
};

export function bundledClauses(slug: string): ClauseTemplate[] {
  return Object.entries(BUNDLED)
    .filter(([path]) => path.includes(`/tenants/${slug}/clauses/`))
    .map(([, t]) => t);
}

export interface DocumentSettingsProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantSlug: string;
  readonly tenantName: string;
  readonly userId: string;
  readonly roles: readonly string[];
  /**
   * One of Company's tabs (C6, decision 844): "details" (company and payment details), "clauses" (clause templates)
   * or "qc" (QC limit defaults), with no page title of its own; all three as one page when not set.
   */
  readonly part?: DocumentPart;
}

export type DocumentPart = 'details' | 'clauses' | 'qc';

const emptyProfile = (tenantId: string, name: string): TenantProfile => ({
  tenant_id: tenantId,
  legal_name: name,
  short_name: name.split(' ')[0] ?? name,
  abn: '',
  incorporated_in: '',
  registered_office: '',
  notices_address: [],
  notices_attention: '',
  notices_email: '',
  footer: '',
  tax_line_enabled: false,
  tax_label: 'GST',
  tax_rate_pct: 10,
});

const emptyPayment = (tenantId: string): TenantPaymentDetails => ({
  tenant_id: tenantId,
  payee_address: [],
  account_name: '',
  bank_name: '',
  bank_address: '',
  bsb: '',
  account_number: '',
  swift: '',
});

/**
 * Company and documents (Settings): legal identity and notices address printed in contracts, the tax line setting
 * (decision 125), the protected payment details (decision 127: tenant admin edits, managers and office staff read),
 * the clause templates (tenant admin) and the QC limit defaults (decision 123).
 */
export function DocumentSettings({
  sb,
  tenantId,
  tenantSlug,
  tenantName,
  userId,
  roles,
  part,
}: DocumentSettingsProps): ReactElement {
  const [docs, setDocs] = useState<TenantDocuments | null>(null);
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [payment, setPayment] = useState<TenantPaymentDetails | null>(null);
  const [qc, setQc] = useState<QcRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const admin = can(roles, 'clauses.write');
  const paymentRead = can(roles, 'payment.read');
  const qcWrite = can(roles, 'qcspec.write');

  const reload = useCallback(() => {
    setError(null);
    loadTenantDocuments(sb, tenantId)
      .then((d) => {
        setDocs(d);
        setProfile(d.profile ?? emptyProfile(tenantId, tenantName));
        setPayment(d.payment ?? emptyPayment(tenantId));
        setQc(d.qcDefaults.find((x) => x.aircraft_type === '')?.rules ?? []);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId, tenantName]);
  useEffect(reload, [reload]);

  async function run(action: () => Promise<void>, done: string): Promise<void> {
    try {
      await action();
      setNotice({ ok: true, text: done });
    } catch (e: unknown) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
    reload();
  }

  const show = (p: DocumentPart): boolean => !part || part === p;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!docs || !profile || !payment) return <Loading what="company and document settings" />;

  const setP = <K extends keyof TenantProfile>(k: K, v: TenantProfile[K]): void =>
    setProfile({ ...profile, [k]: v });
  const setPay = <K extends keyof TenantPaymentDetails>(k: K, v: TenantPaymentDetails[K]): void =>
    setPayment({ ...payment, [k]: v });
  const lines = (s: string): string[] =>
    s
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
  const bundled = bundledClauses(tenantSlug).filter(
    (t) => !docs.clauses.some((c) => c.kind === t.kind && c.version === t.version),
  );

  return (
    <section className={part ? 'stack' : 'page'}>
      {part ? (
        <SectionHeader title={PART_TITLE[part]} subtitle={PART_WORDS[part]} />
      ) : (
        <PageHeader
          title="Company and documents"
          subtitle="What quotes and contracts print about the company."
        />
      )}
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}

      {show('details') && (
        <form
          className="panel stack"
          aria-label="Company details"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => saveProfile(sb, profile, userId), 'Company details saved.');
          }}
        >
          <h2 className="heading">Company details</h2>
          <fieldset className="fieldset form-grid" disabled={!admin}>
            <TextField
              label="Legal name"
              value={profile.legal_name}
              onChange={(v) => setP('legal_name', v)}
            />
            <TextField
              label="Short name in clauses"
              hint="For example Thomson."
              value={profile.short_name}
              onChange={(v) => setP('short_name', v)}
            />
            <TextField label="ABN" spellCheck={false} value={profile.abn} onChange={(v) => setP('abn', v)} />
            <TextField
              label="Incorporated in (state)"
              value={profile.incorporated_in}
              onChange={(v) => setP('incorporated_in', v)}
            />
            <TextField
              label="Registered office"
              value={profile.registered_office}
              onChange={(v) => setP('registered_office', v)}
            />
            <TextArea
              label="Notices address"
              hint="One line per row."
              value={profile.notices_address.join('\n')}
              onChange={(v) => setP('notices_address', lines(v))}
              rows={3}
            />
            <TextField
              label="Notices attention"
              value={profile.notices_attention}
              onChange={(v) => setP('notices_attention', v)}
            />
            <TextField
              label="Notices email"
              type="email"
              value={profile.notices_email}
              onChange={(v) => setP('notices_email', v)}
            />
            <TextField label="Document footer" value={profile.footer} onChange={(v) => setP('footer', v)} />
            <CheckField
              label="Show a tax line and the total including tax on quotes"
              checked={profile.tax_line_enabled}
              onChange={(v) => setP('tax_line_enabled', v)}
            />
            <TextField label="Tax name" value={profile.tax_label} onChange={(v) => setP('tax_label', v)} />
            <NumberField
              label="Tax rate"
              unit="%"
              value={profile.tax_rate_pct}
              onChange={(v) => setP('tax_rate_pct', v ?? 0)}
            />
          </fieldset>
          {admin && (
            <div className="row">
              <Button type="submit" variant="primary">
                Save company details
              </Button>
            </div>
          )}
        </form>
      )}

      {show('details') && paymentRead && (
        <form
          className="panel stack"
          aria-label="Payment details"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => savePayment(sb, payment, userId), 'Payment details saved.');
          }}
        >
          <h2 className="heading">Payment details</h2>
          <p className="body muted">
            Printed in section 5.4 of the full and the OP contract. Only the tenant admin changes them;
            managers and office staff can read them.
          </p>
          <fieldset className="fieldset form-grid" disabled={!can(roles, 'payment.write')}>
            <TextArea
              label="Pay invoices to (address)"
              value={payment.payee_address.join('\n')}
              onChange={(v) => setPay('payee_address', lines(v))}
              rows={3}
            />
            <TextField
              label="Account name"
              value={payment.account_name}
              onChange={(v) => setPay('account_name', v)}
            />
            <TextField label="Bank" value={payment.bank_name} onChange={(v) => setPay('bank_name', v)} />
            <TextField
              label="Bank address"
              value={payment.bank_address}
              onChange={(v) => setPay('bank_address', v)}
            />
            <TextField
              label="BSB"
              spellCheck={false}
              value={payment.bsb}
              onChange={(v) => setPay('bsb', v)}
            />
            <TextField
              label="Account number"
              spellCheck={false}
              value={payment.account_number}
              onChange={(v) => setPay('account_number', v)}
            />
            <TextField
              label="SWIFT code"
              spellCheck={false}
              value={payment.swift}
              onChange={(v) => setPay('swift', v)}
            />
          </fieldset>
          {can(roles, 'payment.write') && (
            <div className="row">
              <Button type="submit" variant="primary">
                Save payment details
              </Button>
            </div>
          )}
        </form>
      )}

      {show('clauses') && (
        <div className="panel stack">
          <h2 className="heading">Clause templates</h2>
          {docs.clauses.length === 0 && (
            <p className="body">
              No clause templates yet. Quotes and contracts cannot be printed until one of each is loaded.
            </p>
          )}
          {docs.clauses.length > 0 && (
            <TableWrap>
              <table className="table">
                <thead>
                  <tr>
                    <th className="label">Document</th>
                    <th className="label">Version</th>
                    <th className="label">In force from</th>
                    <th className="label">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.clauses.map((c) => (
                    <tr key={c.id}>
                      <td>{c.kind === 'quote' ? 'Quote' : 'Contract'}</td>
                      <td className="data">{c.version}</td>
                      <td className="data">{c.effective_from}</td>
                      <td>{c.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
          {admin &&
            bundled.map((t) => (
              <div key={`${t.kind}:${t.version}`} className="row row--wrap">
                <p className="body">
                  Starting {t.kind} clauses “{t.version}”: {t.source}
                </p>
                <Button
                  onClick={() =>
                    void run(
                      () => addClauseVersion(sb, tenantId, t, userId),
                      `${t.kind === 'quote' ? 'Quote' : 'Contract'} clauses ${t.version} loaded.`,
                    )
                  }
                >
                  Load {t.kind} clauses
                </Button>
              </div>
            ))}
        </div>
      )}

      {show('qc') && (
        <div className="panel stack">
          <QcRulesEditor
            rules={qc.length ? qc : BENZ_F26048_QC_RULES.map((r) => ({ ...r }))}
            onChange={setQc}
            issues={[]}
          />
          <p className="body muted">
            New quotes start with these limits. The Benz F26048 values are offered until the company saves its
            own; on a Cessna 208 the noise rule applies to the tail sensor.
          </p>
          {qcWrite && (
            <div className="row">
              <Button
                variant="primary"
                onClick={() =>
                  void run(
                    () => saveQcDefaults(sb, tenantId, '', qc.length ? qc : BENZ_F26048_QC_RULES, userId),
                    'QC limit defaults saved.',
                  )
                }
              >
                Save QC defaults
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
